import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from './FormContext';
import HeaderComponent from './HeaderComponent';
import companylogo from '../snlpic.png';
import '../shared-styles.css';

// Define form flow (steps) configuration
export const formFlow = [
  {
    id: 'photo-section',
    path: '/photo',
    nextId: 'personal-info-section',
    continueText: 'Continue to Personal Information',
    stepNumber: 1,
    stepName: 'Upload Photo Head Shot',
    fields: ['photo'] // Fields in this section for validation checking
  },
  {
    id: 'personal-info-section',
    path: '/personal',
    nextId: 'contact-info-section',
    continueText: 'Continue to Contact Information',
    stepNumber: 2,
    stepName: 'Personal Information',
    fields: ['businessName', 'firstName', 'surname']
  },
  {
    id: 'contact-info-section',
    path: '/contact',
    nextId: 'police-check-section',
    continueText: 'Continue to Police Check',
    stepNumber: 3,
    stepName: 'Contact Information',
    fields: ['streetAddress', 'suburb', 'state', 'postcode', 'email', 'mobile']
  },
  {
    id: 'police-check-section',
    path: '/police-check',
    nextId: 'work-dates-section',
    continueText: 'Continue to Work Dates',
    stepNumber: 4,
    stepName: 'Police Check',
    fields: ['policeCheck', 'policeCheckIssue']
  },
  {
    id: 'work-dates-section',
    path: '/work-dates',
    nextId: 'wwcc-section',
    continueText: 'Continue to Working With Children',
    stepNumber: 5,
    stepName: 'Work Dates',
    fields: ['startWork', 'dateOfBirth']
  },
  {
    id: 'wwcc-section',
    path: '/wwcc',
    nextId: 'visa-section',
    continueText: 'Continue to Visa Information',
    stepNumber: 6,
    stepName: 'Working With Children Check',
    fields: ['hasWWCC', 'wwcc', 'wwccExpiry']
  },
  {
    id: 'visa-section',
    path: '/visa',
    nextId: 'documents-section',
    continueText: 'Continue to Documents',
    stepNumber: 7,
    stepName: 'Visa Information',
    fields: ['visaType', 'visaExpiry','visaDocument']
  },
  {
    id: 'documents-section',
    path: '/documents',
    nextId: 'work-status-section',
    continueText: 'Continue to Work Status',
    stepNumber: 8,
    stepName: 'Documents',
    fields: ['passportDocument']
  },
  {
    id: 'work-status-section',
    path: '/work-status',
    nextId: 'bank-info-section',
    continueText: 'Continue to Banking Information',
    stepNumber: 9,
    stepName: 'Work Status',
    fields: ['workStatus', 'tfnNumber', 'superFundName', 'superFundAccountName', 'membershipNumber', 'abn']
  },
  {
    id: 'bank-info-section',
    path: '/bank-info',
    nextId: 'final-submit-section',
    continueText: 'Continue to Review & Submit',
    stepNumber: 10,
    stepName: 'Banking Information',
    fields: ['bankAccountName', 'bsbNumber', 'bankAccountNumber']
  },
  {
    id: 'final-submit-section',
    path: '/review',
    nextId: null,
    continueText: 'Submit',
    stepNumber: 11,
    stepName: 'Review & Submit',
    fields: []
  }
];

// Friendly labels for required-field error messages.
// Falls back to capitalising the field key if not listed here.
const FIELD_LABELS = {
  streetAddress: 'Street address',
  suburb: 'Suburb',
  state: 'State',
  postcode: 'Postcode',
  businessName: 'Business name',
  firstName: 'First name',
  surname: 'Surname',
  policeCheck: 'Police check',
  policeCheckIssue: 'Police check issue date',
  startWork: 'Start work date',
  dateOfBirth: 'Date of birth',
  bankAccountName: 'Bank account name',
  bsbNumber: 'BSB number',
  bankAccountNumber: 'Bank account number',
};

const fieldLabel = (field) =>
  FIELD_LABELS[field] || field.charAt(0).toUpperCase() + field.slice(1);

const FormWizard = ({ children }) => {
  const navigate = useNavigate();
  const { 
    currentStep, 
    setCurrentStep,
    formData,
    formErrors,
    isSubmitting,
    submitMessage,
    setModifiedFields,
    setFormErrors,
    userType,
    userData,

  } = useFormContext();
  
  const [showLoadingTransition, setShowLoadingTransition] = useState(true);
  
  // Initialize form with loading transition
  useEffect(() => {
    // Show loading transition briefly then hide it
    setTimeout(() => {
      setShowLoadingTransition(false);
    }, 1000);
  }, []);
  
  // Function to handle going back to profile
  const handleBack = () => {
    navigate('/landingprofile', { state: { userType, data: userData } });
  };
  
  // Get current step information
  const getCurrentStep = () => formFlow[currentStep];
  
  // Check if current section has any validation errors
  const canProceedToNextStep = () => {
    const currentStepData = getCurrentStep();
    const currentSectionFields = currentStepData.fields;
    
    // Mark all fields in this section as modified when trying to proceed
    // This ensures validation errors show up for empty required fields
    setModifiedFields(prev => {
      const newSet = new Set(prev);
      currentSectionFields.forEach(field => newSet.add(field));
      return newSet;
    });
    

    // Check for empty required fields and add errors
  let newErrors = {...formErrors};
  
  // Example for WWCC section
  if (currentStepData.id === 'wwcc-section') {
    if (formData.hasWWCC === 'Yes') {
      // Only validate WWCC number and expiry if user has WWCC
      if (!formData.wwcc) {
        newErrors.wwcc = 'WWCC number is required when you have a WWCC';
      }
      if (!formData.wwccExpiry) {
        newErrors.wwccExpiry = 'WWCC expiry date is required';
      }
    } else if (formData.hasWWCC === 'No') {
      // If "No" is selected, remove any errors for these fields
      delete newErrors.wwcc;
      delete newErrors.wwccExpiry;
    } else if (!formData.hasWWCC) {
      // If nothing is selected, that's an error
      newErrors.hasWWCC = 'Please select whether you have a WWCC';
    }
  } else if (currentStepData.id === 'work-status-section') {
    if (formData.workStatus === 'TFN') {
      
      // Only validate WWCC number and expiry if user has WWCC
      if (!formData.tfnNumber) {
        newErrors.tfnNumber = 'TFN number is required when you are employed as TFN';
      }
      if (!formData.superFundName) {
        newErrors.superFundName = 'Super fund name is required';
      }
      if (!formData.superFundAccountName) {
        newErrors.superFundAccountName = 'Super fund account name is required';
      }
      if (!formData.membershipNumber) {
        newErrors.membershipNumber = 'Super membership number is required';
      }
      delete newErrors.abn;
    } else if (formData.workStatus === 'ABN') {
      // If "No" is selected, remove any errors for these fields
      delete newErrors.tfnNumber;
      delete newErrors.superFundName;
      delete newErrors.superFundAccountName;
      delete newErrors.membershipNumber;
      if (!formData.abn) {
        newErrors.abn = 'ABN number is required';
      }
    } 
  } else if (currentStepData.id === 'visa-section') {
    if (formData.visaType === 'Citizen') {
        // For Citizens, remove visa document and expiry requirements
        delete newErrors.visaExpiry;
        delete newErrors.visaDocument;
      } else if (formData.visaType) {
        // For all other visa types, check if required fields are provided
        if (!formData.visaExpiry) {
          newErrors.visaExpiry = 'Visa expiry date is required for non-citizens';
        }
        if (!formData.visaDocument) {
          newErrors.visaDocument = 'Visa document is required for non-citizens';
        }
      } else {
        // If no visa type is selected
        newErrors.visaType = 'Visa type is required';
      }
    
    
    } else if (currentStepData.id === 'documents-section') {
    if (formData.visaType === 'Citizen') {
    // Citizens don't need to provide passport document
    delete newErrors.passportDocument;
      } else if (formData.visaType && !formData.passportDocument) {
        // Non-citizens must provide passport
        newErrors.passportDocument = 'Passport document is required for non-citizens';
      }
    
    } else {
    // For fields that are always required
    currentSectionFields.forEach(field => {
      // Skip optional fields or fields that depend on other values
      if (isOptionalField(field, formData)) {
        return;
      }
      

      // Check if field is empty and required
      if (!formData[field] && !newErrors[field]) {
        newErrors[field] = `${fieldLabel(field)} is required`;
      }
    });

    // Extra check for the contact section: postcode must be 4 digits
    if (currentStepData.id === 'contact-info-section') {
      if (formData.postcode && formData.postcode.length !== 4) {
        newErrors.postcode = 'Postcode must be 4 digits';
      }
    }
  }
  
  // If we found new errors, update the errors state
  if (JSON.stringify(newErrors) !== JSON.stringify(formErrors)) {
    setFormErrors(newErrors);
  }
  
  // Check if any of the current section's fields have validation errors
  const hasErrors = currentSectionFields.some(field => {
    return !!newErrors[field];
  });
  
  return !hasErrors;
};

// Helper function to determine if a field is optional based on form state
const isOptionalField = (field, formData) => {
  // WWCC related fields are optional if hasWWCC is 'No'
  if ((field === 'wwcc' || field === 'wwccExpiry') && formData.hasWWCC === 'No') {
    return true;
  }
  
  // Visa document is optional if visaType is 'Australian Citizen'
  if (field === 'visaDocument' && formData.visaType === 'Australian Citizen') {
    return true;
  }
  
  // Add other conditional logic here as needed
  
  return false;
};
  
  // Handle navigation to next step
  const handleNext = () => {
    // First check if we can proceed based on validation
    if (!canProceedToNextStep()) {
      return;
    }
    
    
    const nextStep = currentStep + 1;
    
    // If there's a next step, go to it
    if (nextStep < formFlow.length) {
      setCurrentStep(nextStep);
      // Navigate to the corresponding route
      //navigate(formFlow[nextStep].path);
    }
  };
  
  // Calculate progress percentage
  const getProgressPercentage = () => {
    return ((currentStep + 1) / formFlow.length) * 100;
  };
  
  // If still loading, show transition
  if (showLoadingTransition) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <img src={companylogo} alt="Company Logo" className="h-16 mx-auto mb-4" />
          <p className="text-gray-600">Loading your form, please wait...</p>
        </div>
      </div>
    );
  }
  
 return (
  <>
    <HeaderComponent
      title="SNL Database Form"
      backLabel="Back to Hub"
      onBack={handleBack}
      showSearch={false}
    />
    
    {/* Outer: full-width dark background, 50px gap from header */}
    <div 
      className="min-h-screen w-full pt-[50px] pb-[50px] px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: "#1a1a2e" }}
    >
      {/* Inner: the white card, capped width and centered */}
      <div 
        className="modal-content bg-white shadow-xl rounded-lg"
        style={{
          width: "100%",
          maxWidth: "896px",  // matches the original max-w-4xl (4xl = 56rem = 896px)
          margin: "0 auto",
        }}
      >
        {/* Header */}
        <div className="modal-header bg-white-50 border-b border-gray-200 flex flex-col items-center px-4 py-3">
          <div className="flex items-center">
            <img src={companylogo} alt="Company Logo" className="h-12 md:h-16 lg:h-20 w-auto mr-3" />
          </div>
          <div className="flex-1 text-center">
            <h1 className="modal-title font-bold py-3 text-xl md:text-2xl lg:text-3xl" id="uploadModalsnlLabel" style={{ color: '#22d3ee' }}>
              SNL Database Form<strong> <span className="text-green-600 text-sm md:text-base lg:text-lg">(please fill in form with English only)</span></strong>
            </h1>
          </div>
          <div className='flex-1 text-center'>
            <p className="text-sm md:text-base" style={{ color: '#ffffff' }}>
              Welcome, <span className="font-medium">{formData.firstName}</span>! 
              Please complete all sections of this form. You'll have a chance to review everything before final submission.
            </p>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="bg-white px-4 py-2 border-b pt-6">
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              id="form-progress-bar"
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
              style={{ width: `${getProgressPercentage()}%` }}
            ></div>
          </div>
          <p id="progress-text" className="text-sm text-gray-600 mt-1 pb-6">
            Step {getCurrentStep().stepNumber} of {formFlow.length}: {getCurrentStep().stepName}
          </p>
        </div>
        
        {/* Submission indicators */}
        {isSubmitting && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-blue-700 flex items-center justify-center">
            <svg className="animate-spin h-5 w-5 mr-3 text-blue-500" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Submitting your information...
          </div>
        )}

        {submitMessage && (
          <div className={`mt-4 p-3 rounded-md ${
            submitMessage.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-700' 
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {submitMessage.message}
          </div>
        )}
        
        {/* Form content area */}
        <div className="modal-body p-6" style={{ maxHeight: '600px', overflowY: 'auto' }}>
          {children}
          
          {/* Navigation buttons */}
          {getCurrentStep().nextId !== null && (
            <div className="mt-6">
              <button
                type="button"
                id="dynamic-continue-btn"
                className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition duration-200"
                onClick={handleNext}
              >
                {getCurrentStep().continueText}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  </>
);
};

export default FormWizard;